import { type ViewContext, useAbroadHome, useAbroadStudents } from '@shkola/api';
import { Link } from '@tanstack/react-router';
import { ChevronDown, LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLook } from '../design/looks';
import { Initials } from '../design/ui';
import { contextLabel } from '../lib/labels';
import { FaceAvatar, useFace } from '../student/home/LineArt';
import { useNavBadges } from './badges';
import { NAV, type NavKey, type NavLayout, navLayout } from './nav';
import { useViewContext } from './ViewContext';
import { BrandWord } from '../design/brand';

/** «З-за кордону» for a family abroad; «Учні за кордоном» for the office and for teachers who have such students. */
function useAbroadNav(ctx: ViewContext | undefined): boolean {
  const family = ctx?.kind === 'student' || ctx?.kind === 'guardian';
  const home = useAbroadHome(family ? ctx.child.memberId : '');
  const taught = useAbroadStudents(ctx?.schoolId ?? '', ctx?.kind === 'teacher');
  if (family) return Boolean(home.data);
  if (ctx?.kind === 'admin') return true;
  return ctx?.kind === 'teacher' && (taught.data?.length ?? 0) > 0;
}

/** The menu of the current role. */
export function useNavLayout(): NavLayout {
  const { current } = useViewContext();
  return navLayout(current, useAbroadNav(current));
}

function ContextSwitcher() {
  const { t } = useTranslation();
  const { contexts, current, select } = useViewContext();
  if (!current || contexts.length < 2) return null;
  return (
    <div className="relative">
      <select
        aria-label={t('context.switch')}
        className="h-10 w-full cursor-pointer appearance-none rounded-full bg-white pr-10 pl-4 text-[14px] font-medium text-ink outline-none focus:ring-2 focus:ring-ink/15"
        value={current.key}
        onChange={(e) => select(e.target.value)}
      >
        {contexts.map((c) => (
          <option key={c.key} value={c.key}>
            {contextLabel(t, c)}
          </option>
        ))}
      </select>
      <ChevronDown
        size={16}
        aria-hidden
        className="pointer-events-none absolute top-1/2 right-4 -translate-y-1/2 text-faint"
      />
    </div>
  );
}

/** «Форми 2»: the student's own face, the one picked on the home. */
function StudentFace({ ctx }: { ctx: Extract<ViewContext, { kind: 'student' }> }) {
  const [face] = useFace(ctx.child.memberId);
  return (
    <FaceAvatar
      face={face}
      initials={`${ctx.child.firstName[0] ?? ''}${ctx.child.lastName[0] ?? ''}`.toUpperCase()}
      size={40}
    />
  );
}

function Me({ ctx, email }: { ctx: ViewContext; email: string | undefined }) {
  const { t } = useTranslation();
  const look = useLook();
  const student = ctx.kind === 'student';
  const name = student ? `${ctx.child.firstName} ${ctx.child.lastName}` : contextLabel(t, ctx);
  return (
    <>
      {student && look === 'shapes2' ? (
        <StudentFace ctx={ctx} />
      ) : (
        <Initials
          name={student ? name : (email ?? '')}
          size={40}
          tone={student ? 'violet' : 'grey'}
        />
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13.5px] font-semibold text-ink">{name}</span>
        {/* no «учень / учениця»: the school does not tell us which */}
        <span className="block truncate text-xs text-muted">
          {student ? `${ctx.child.class?.label ?? ''} · ${ctx.schoolName}` : email}
        </span>
      </span>
    </>
  );
}

/**
 * A section of the menu (ref. the school calendar): a dark line icon and the name, the open one on a
 * white pill; the count of what is unread in it on the right.
 */
function Item({ id, count, onNavigate }: { id: NavKey; count?: number; onNavigate: () => void }) {
  const { t } = useTranslation();
  const look = useLook();
  const Icon = NAV[id].icon;
  if (look === 'glass' || look === 'glass2')
    return <GlassItem id={id} count={count} onNavigate={onNavigate} />;
  if (look === 'shapes2') return <SoftItem id={id} count={count} onNavigate={onNavigate} />;
  return (
    <Link
      to={NAV[id].to}
      onClick={onNavigate}
      activeOptions={{ exact: id === 'home', includeSearch: false }}
      className="flex h-8 shrink-0 items-center gap-3 rounded-full px-3 text-[14px] text-ink transition-colors duration-200 hover:bg-white/60"
      activeProps={{
        // «Форми»: the open one on lime
        className:
          look === 'shapes'
            ? '!bg-[#DDF56B] font-medium'
            : '!bg-white font-medium shadow-[0_1px_2px_rgb(23_23_27/0.05)]',
      }}
    >
      <Icon aria-hidden size={17} strokeWidth={1.6} className="shrink-0 text-ink-soft" />
      <span className="truncate">{t(`nav.${id}`)}</span>
      {count !== undefined && count > 0 && (
        <span className="ml-auto flex h-[18px] min-w-[18px] shrink-0 items-center justify-center rounded-full bg-primary px-1 text-[11px] font-semibold text-white">
          {count > 99 ? '99+' : count}
        </span>
      )}
    </Link>
  );
}

/**
 * A section in «Скло» (ref. «Aella Credit», where the menu is round icons only): the icon in a frosted
 * circle and the name beside it; the open one — a black circle with a white icon.
 */
function GlassItem({
  id,
  count,
  onNavigate,
}: {
  id: NavKey;
  count?: number;
  onNavigate: () => void;
}) {
  const { t } = useTranslation();
  const Icon = NAV[id].icon;
  return (
    <Link
      to={NAV[id].to}
      onClick={onNavigate}
      activeOptions={{ exact: id === 'home', includeSearch: false }}
      className="group flex h-9 shrink-0 items-center gap-3 rounded-full pr-3 text-[14px] text-ink-soft transition-colors duration-200 hover:text-ink"
      activeProps={{ className: 'is-open !text-ink font-medium' }}
    >
      <span
        className={`relative flex size-8 shrink-0 items-center justify-center rounded-full bg-white/55 text-ink-soft shadow-[inset_0_0_0_1px_rgb(255_255_255/0.8)] transition-colors group-hover:bg-white group-[.is-open]:text-white group-[.is-open]:bg-primary group-[.is-open]:shadow-none`}
      >
        <Icon aria-hidden size={15} strokeWidth={1.7} />
        {count !== undefined && count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 size-2.5 rounded-full bg-[#F27386] ring-2 ring-[#E4E1E2]" />
        )}
      </span>
      <span className="truncate">{t(`nav.${id}`)}</span>
      {count !== undefined && count > 0 && (
        <span className="ml-auto text-[12px] font-semibold text-[#8E9197]">
          {count > 99 ? '99+' : count}
        </span>
      )}
    </Link>
  );
}

/**
 * A section in «Форми 2» (his edits of 28.10: «меню выбивается из стилистики»; «низкий контраст»; «иконки в
 * скруглённых квадратных плашечках — не нравится»; «активный пункт и уведомления одним цветом — уведомления
 * другим, оранжевым, градиентным»): the icon bare, the words in the full ink; the open one a pill of the
 * paper a shade darker (the dark one «слишком контрастный», then «как фон, только чуть темнее»); the count a
 * soft rectangle in the orange gradient. The icons and the parts' names
 * on one line.
 */
function SoftItem({
  id,
  count,
  onNavigate,
}: {
  id: NavKey;
  count?: number;
  onNavigate: () => void;
}) {
  const { t } = useTranslation();
  const Icon = NAV[id].icon;
  return (
    <Link
      to={NAV[id].to}
      onClick={onNavigate}
      activeOptions={{ exact: id === 'home', includeSearch: false }}
      className="soft-item flex h-[34px] shrink-0 items-center gap-3 rounded-full pr-1.5 pl-3 text-[14px] transition-colors duration-200"
      activeProps={{ className: 'is-open font-medium' }}
    >
      <Icon aria-hidden size={18} strokeWidth={1.7} className="soft-icon shrink-0" />
      <span className="truncate">{t(`nav.${id}`)}</span>
      {count !== undefined && count > 0 && (
        <span className="soft-count ml-auto inline-flex h-[22px] min-w-[22px] shrink-0 items-center justify-center rounded-[8px] px-1.5 text-[11.5px] font-semibold">
          {count > 99 ? '99+' : count}
        </span>
      )}
    </Link>
  );
}

/** A part of the menu: its small grey name (ref. «Main menu») and its sections, always open. */
function Part({
  label,
  items,
  badges,
  onNavigate,
}: {
  label?: string;
  items: NavKey[];
  badges: Partial<Record<NavKey, number>>;
  onNavigate: () => void;
}) {
  return (
    <div>
      {label && (
        <p className="nav-label px-3 pb-1.5 text-[12px] leading-4 text-[#878c92]">{label}</p>
      )}
      <nav className="flex flex-col gap-0.5">
        {items.map((key) => (
          <Item key={key} id={key} count={badges[key]} onNavigate={onNavigate} />
        ))}
      </nav>
    </div>
  );
}

/** The logo PΛRTΛ: over the menu on a computer, on top of the menu on a phone. */
export function Logo() {
  return (
    <span className="flex h-8 items-center text-ink">
      <BrandWord height={15} />
    </span>
  );
}

/**
 * The menu of every role on the frosted plate (design v4, 27.10): every part open, nothing folded.
 * It follows the white panel beside it: the first part starting near the panel's top, the account
 * ending at the panel's bottom; the logo stands above it in the bar (in the menu itself on a phone).
 */
export function Sidebar({
  ctx,
  email,
  logo = false,
  onNavigate,
  onSignOut,
}: {
  ctx: ViewContext | undefined;
  email: string | undefined;
  /** the phone's menu, which has no bar above it */
  logo?: boolean;
  onNavigate: () => void;
  onSignOut: () => void;
}) {
  const { t } = useTranslation();
  const layout = useNavLayout();
  const badges = useNavBadges();
  const look = useLook();
  const settings = look === 'shapes2' && ctx?.kind === 'student';
  return (
    <div className={`flex h-full flex-col ${logo ? 'px-5 pb-3' : 'px-3 pt-4 pb-2'}`}>
      {logo && (
        <div className="flex h-16 shrink-0 items-center px-1">
          <Logo />
        </div>
      )}
      <div
        className={`no-scrollbar flex min-h-0 flex-1 flex-col overflow-y-auto ${look === 'shapes2' ? 'gap-3.5' : 'gap-5'}`}
      >
        {ctx && ctx.kind !== 'student' && (
          <div>
            <p className="truncate px-3 pb-1.5 text-[12px] leading-4 text-[#878c92]">
              {ctx.schoolName}
            </p>
            <ContextSwitcher />
          </div>
        )}
        <Part
          label={t('nav.groups.main')}
          items={layout.pinned}
          badges={badges}
          onNavigate={onNavigate}
        />
        {layout.groups.map((g) => (
          <Part
            key={g.label}
            label={t(`nav.groups.${g.label}`)}
            items={g.items}
            badges={badges}
            onNavigate={onNavigate}
          />
        ))}
        <div className={`mt-auto flex flex-col ${look === 'shapes2' ? 'gap-2.5' : 'gap-4'}`}>
          <Part
            items={settings ? [...layout.footer, 'settings'] : layout.footer}
            badges={badges}
            onNavigate={onNavigate}
          />
          {ctx && (
            <div className={`flex items-center gap-3 ${look === 'shapes2' ? '' : 'px-1'}`}>
              {settings ? (
                // «Форми 2»: the profile opens the settings (the avatar is picked there)
                <Link
                  to="/settings"
                  onClick={onNavigate}
                  className="-my-1 flex min-w-0 flex-1 items-center gap-3 rounded-full py-1 pr-2 transition-colors hover:bg-white/75"
                >
                  <Me ctx={ctx} email={email} />
                </Link>
              ) : (
                <Me ctx={ctx} email={email} />
              )}
              <button
                type="button"
                onClick={onSignOut}
                aria-label={t('common.signOut')}
                title={t('common.signOut')}
                className="rounded-full p-2 text-muted transition hover:bg-white/60 hover:text-ink"
              >
                <LogOut size={16} strokeWidth={1.7} aria-hidden />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
